import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '../../../../../app/store';
import { selectCurrentVerification } from '../../../data/verificationSlice/selectors';

/**
 * Estimate-tab derived selectors (spec 006). Read from the shared verification slice; the header
 * totals are derived (never stored). The evaluation selectors expose the transient, server-evaluated
 * matches/alerts/bypass — the client holds no rule codes/thresholds (FR-047).
 */

const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;
const slice = (state: RootState) => state.verification;

export const selectProcedures = createSelector(
  selectCurrentVerification,
  (c) => c?.procedures ?? [],
);

/** Total Reimbursement = Σ reimbursement (includes self-pay rows, FR-005). */
export const selectTotalReimbursement = createSelector(selectProcedures, (procs) =>
  round2(procs.reduce((sum, p) => sum + p.reimbursement, 0)),
);

/** Total Contribution = Σ reimbursement − Σ implantCost (includes self-pay rows, FR-005). */
export const selectTotalContribution = createSelector(selectProcedures, (procs) =>
  round2(procs.reduce((sum, p) => sum + p.reimbursement - p.implantCost, 0)),
);

export const selectDiagnosisCodes = createSelector(
  selectCurrentVerification,
  (c) => c?.diagnosisCodes,
);

export const selectImplantsNotUsed = createSelector(
  selectCurrentVerification,
  (c) => c?.implantsNotUsed ?? false,
);

export const selectPatientResponsibility = createSelector(
  selectCurrentVerification,
  (c) => c?.patientResponsibility,
);

export const selectApproval = createSelector(selectCurrentVerification, (c) => c?.approval);

export const selectConfirmedPayments = createSelector(
  selectCurrentVerification,
  (c) => c?.benefits?.paidToPhysician ?? 0,
);

export const selectUncoveredAmount = createSelector(
  selectCurrentVerification,
  (c) => c?.benefits?.uncoveredAmount ?? 0,
);

// ── Transient evaluation (presentational) ──
export const selectEstimateEvaluation = createSelector(slice, (s) => s.evaluation);
export const selectEstimateAlerts = createSelector(slice, (s) => s.evaluation?.alerts ?? []);
export const selectEstimateMatches = createSelector(slice, (s) => s.evaluation?.matches ?? null);
export const selectBypassSuggestion = createSelector(
  slice,
  (s) => s.evaluation?.bypassSuggestion ?? null,
);
export const selectDisableCodeSetMatching = createSelector(
  slice,
  (s) => s.disableCodeSetMatching,
);
